import React, { FC, useEffect, useState } from 'react';
import RestaurantMapMarker from './components/MapMarkers/RestaurantMapMarker';
import CourierMapMarker from './components/MapMarkers/CourierMapMarker';
import {
  IOrder,
  ICourierSnap,
  IDeliveryStatus,
  IRoute,
  IWaypoint,
} from '../../models/model-interface';
import { DeliveryDashboardScreens, OrderStatus } from '../../models/model-enum';
import { useShallow } from 'zustand/react/shallow';
import { useKeycloakUserState } from '@src/shared/context/keycloak-user';
import useDashboardStore from '../../hooks/use-dashboard-store';
import useDeviceDetect from '@src/shared/hooks/use-device-detect';
import { DETAILED_ZOOM_LEVEL, INITIAL_ZOOM_LEVEL } from '../../models/model-constants';
import { useDashboardMapStore } from '../../store/dashboardMapStore';
import { useDashboardUIStore } from '../../store/dashboardUIStore';
import { ICourier } from '@src/pages/Deliveries/context/couriers';
import useDeliveryDashboadAnalytics from '../../tracking/use-delivery-dashboard-analytics';
import { DASHBOARD_ORDER_FILTER_VALUES } from '../DashboardOrderList/components/DashboardOrderListFilter';
import useBoundDashboardStore from '../../store/dashboardGlobalStore';
import useSupercluster from 'use-supercluster';
import ClusterRenderer from './components/ClusterRenderer';
import { useCommonPersistentStore } from '@src/shared/store/common-persistent-store';
import OrderClusterTutorial from '@src/shared/components/Tutorial/tutorials/OrderClusterTutorial';
import { useTutorialActions, useTutorialState } from '@src/shared/context/tutorial';
import { TUTORIAL_ORDER_CLUSTER } from '@src/shared/components/Tutorial/TutorialsContainer';
import { useElementCreated } from '@src/shared/hooks/use-element-created';
import dayjs from 'dayjs';
import { isWithinPreOrderWindow } from '@src/shared/utils';

const MOBILE_PAN_OFFSET = 70;
const CLUSTER_MIN_POINTS_ACTIVE = 2;
const CLUSTER_MIN_POINTS_INACTIVE = 9999;
const ZOOM_LEVEL_THRESHOLD_LOW = 11;
const ZOOM_LEVEL_THRESHOLD_HIGH = 13;
const ZOOM_LEVEL_THRESHOLD_MAX = 16;
const ZOOM_STAGE_1 = 1;
const ZOOM_STAGE_2 = 2;
const ZOOM_STAGE_3 = 3;
const ZOOM_STAGE_RADIUS_1 = 600;
const ZOOM_STAGE_RADIUS_2 = 1000;
const ZOOM_STAGE_RADIUS_3 = 1500;

type TDeliveryDashboardMapMarkersProps = {
  defaultMapMarkerPosition: google.maps.LatLngLiteral;
  bounds: any;
  zoom: any;
  mapInstance: google.maps.Map | null;
};

export const DeliveryDashboardMapMarkers: FC<TDeliveryDashboardMapMarkersProps> = ({
  defaultMapMarkerPosition,
  bounds,
  zoom,
  mapInstance,
}) => {
  const { startTutorial } = useTutorialActions();
  const tutorialState = useTutorialState();
  const {
    setActiveCourierId,
    setActiveOrderHash,
    setActiveScreen,
    activeOrderHash,
    activeCourierId,
    activeOrderListFilter,
    activePostCodeFilterValues,
  } = useDashboardUIStore();
  const { activeCouriers, orderList, courierStatus, courierRoute, courierNextRoute, serverTime } =
    useDashboardStore();
  const { isResolutionMobile } = useDeviceDetect();

  const { settings } = useCommonPersistentStore();
  const { createdElement } = useElementCreated('.cluster-element');

  const { trackOrderDetailViewViaMap, trackCourierDetailViewViaMap } =
    useDeliveryDashboadAnalytics();

  const [setMapReFocusToCenter, setMapPanOffset, setMapCenter, setMapZoomLevel] =
    useDashboardMapStore(
      useShallow((state) => [
        state.setMapReFocusToCenter,
        state.setMapPanOffset,
        state.setMapCenter,
        state.setMapZoomLevel,
      ])
    );

  const zoomStageRadiusValues = {
    [ZOOM_STAGE_1]: ZOOM_STAGE_RADIUS_1,
    [ZOOM_STAGE_2]: ZOOM_STAGE_RADIUS_2,
    [ZOOM_STAGE_3]: ZOOM_STAGE_RADIUS_3,
  };

  const { restaurant } = useKeycloakUserState();
  const { getOrderEtaByUUID } = useBoundDashboardStore();
  const [clusterRadius, setClusterRadius] = useState(zoomStageRadiusValues[ZOOM_STAGE_1]);
  const [zoomStageForRadius, setZoomStageForRadius] = useState(ZOOM_STAGE_1);

  const getActiveOrderByCourierId = (courierId: string) => {
    return orderList?.find(
      (o: IOrder) => o.courier?.id === courierId && o.status === OrderStatus.InDelivery
    );
  };

  const setCalculatedZoomStageForRadius = (zoom: number) => {
    if (zoom <= ZOOM_LEVEL_THRESHOLD_LOW) {
      setZoomStageForRadius(ZOOM_STAGE_3);
    } else if (zoom > ZOOM_LEVEL_THRESHOLD_LOW && zoom <= ZOOM_LEVEL_THRESHOLD_HIGH) {
      setZoomStageForRadius(ZOOM_STAGE_2);
    } else if (zoom > ZOOM_LEVEL_THRESHOLD_HIGH && zoom < ZOOM_LEVEL_THRESHOLD_MAX) {
      setZoomStageForRadius(ZOOM_STAGE_1);
    }
  };

  const onOrderMapMarkerClick = (order: IOrder) => {
    if (!activeOrderHash || activeOrderHash !== order.hash) {
      setActiveCourierId(null);
      setActiveOrderHash(order.hash);
      setActiveScreen(DeliveryDashboardScreens.ORDER_DETAIL_SCREEN);
      setMapCenter({
        lat: order.location.latitude,
        lng: order.location.longitude,
      });
      setMapZoomLevel(DETAILED_ZOOM_LEVEL);
      isResolutionMobile ? setMapPanOffset(MOBILE_PAN_OFFSET) : null;
      trackOrderDetailViewViaMap(order.hash, order.uuid);
    } else {
      setActiveOrderHash(null);
      setMapZoomLevel(INITIAL_ZOOM_LEVEL);
      setActiveScreen(DeliveryDashboardScreens.MANAGE_DELIVERY_SCREEN);
    }
  };

  const onCourierMapMarkerClick = (courier: ICourier) => {
    if (!activeCourierId || activeCourierId !== courier.id) {
      setActiveOrderHash(null);
      setActiveCourierId(courier.id);
      setActiveScreen(DeliveryDashboardScreens.COURIER_DETAIL_SCREEN);
      trackCourierDetailViewViaMap(courier.id);
    } else {
      setActiveOrderHash(null);
      setActiveCourierId(null);
      setActiveScreen(DeliveryDashboardScreens.MANAGE_DELIVERY_SCREEN);
    }
  };

  const orderListFilterLogic = (order: IOrder) => {
    if (!order.location?.latitude && !order.location?.longitude) {
      return false;
    }

    if (!isWithinPreOrderWindow(order.expectedDeliveryTime, order.isPreorder)) {
      return false;
    }

    if (activePostCodeFilterValues.length > 0) {
      if (!activePostCodeFilterValues.includes(order.customer.address.postcode)) {
        return false;
      }
    }

    if (activeCourierId && courierRoute) {
      const currentRoutePoints =
        courierRoute?.find((r: IRoute) => r.courier.id === activeCourierId)?.waypoints || [];
      const nextRoutePoints =
        courierNextRoute?.find((r: IRoute) => r.courier.id === activeCourierId)?.waypoints || [];
      const mergedWaypoints: Array<IWaypoint> = [...currentRoutePoints, ...nextRoutePoints];
      const orderReferences = mergedWaypoints.map((wp: IWaypoint) => {
        return wp.order.reference;
      });
      return orderReferences.includes(order.reference);
    }
    if (!activeOrderHash && activeOrderListFilter === DASHBOARD_ORDER_FILTER_VALUES.DELIVERED) {
      return order.status === OrderStatus.Delivered;
    }

    if (!activeOrderHash && activeOrderListFilter === DASHBOARD_ORDER_FILTER_VALUES.ON_THE_WAY) {
      if (order.status === OrderStatus.Assigned) {
        return order.status === OrderStatus.Assigned;
      }
      return order.status === OrderStatus.InDelivery;
    }

    if (!activeOrderHash && activeOrderListFilter === DASHBOARD_ORDER_FILTER_VALUES.UNASSIGNED) {
      return order.status === OrderStatus.Unassigned;
    }

    if (activeOrderHash) {
      return order.hash === activeOrderHash ? order.status : false;
    }

    return true;
  };

  const courierListFilterLogic = (courier: ICourierSnap) => {
    if (activeCourierId) {
      return courier.id === activeCourierId;
    }
    return true;
  };

  const filteredOrders = () => {
    return orderList?.filter(orderListFilterLogic) || [];
  };

  const getPoints = () => {
    return filteredOrders().map((order) => ({
      type: 'Feature',
      properties: {
        cluster: false,
        orderData: order,
      },
      geometry: {
        type: 'Point',
        coordinates: [order.location.longitude, order.location.latitude],
      },
    }));
  };

  const applyCompatibleZoomToCluster = (cluster: any) => {
    const children = supercluster.getLeaves(cluster.id);
    const bounds = new google.maps.LatLngBounds();
    children.forEach((p: any) => {
      bounds.extend({ lat: p.geometry.coordinates[1], lng: p.geometry.coordinates[0] });
    });
    mapInstance?.fitBounds(bounds);
  };

  const onMapClusterClickHandler = (cluster: any) => {
    applyCompatibleZoomToCluster(cluster);
  };

  const canRunClusteringTutorial = (withoutStorage: boolean = false) => {
    const today = dayjs().format('YYYY-MM-DD');
    const clusterTutorialStoredData = localStorage.getItem(
      'delivery_dashboard_order_cluster_tutorial'
    );
    const parsedData = clusterTutorialStoredData ? JSON.parse(clusterTutorialStoredData) : [];
    if (parsedData.length === 2 || parsedData.includes(today)) {
      return false;
    } else if (!parsedData.includes(today)) {
      if (withoutStorage) return true;
      parsedData.push(today);
      localStorage.setItem('delivery_dashboard_order_cluster_tutorial', JSON.stringify(parsedData));
      return true;
    }
    return false;
  };

  const { clusters, supercluster } = useSupercluster({
    points: getPoints(),
    bounds,
    zoom,
    options: {
      radius: clusterRadius,
      minPoints: settings.showOrderClusters
        ? CLUSTER_MIN_POINTS_ACTIVE
        : CLUSTER_MIN_POINTS_INACTIVE,
      maxZoom: INITIAL_ZOOM_LEVEL - 1,
    },
  });

  useEffect(() => {
    window.requestAnimationFrame(() => {
      setMapZoomLevel(INITIAL_ZOOM_LEVEL + 1);
      setMapZoomLevel(INITIAL_ZOOM_LEVEL);
    });
  }, []);

  useEffect(() => {
    setCalculatedZoomStageForRadius(zoom);
  }, [zoom]);

  useEffect(() => {
    const calculatedRadius =
      zoomStageRadiusValues[zoomStageForRadius as keyof typeof zoomStageRadiusValues];
    setClusterRadius(calculatedRadius);
  }, [zoomStageForRadius]);

  useEffect(() => {
    if (createdElement) {
      const tutorialCanBeShown = canRunClusteringTutorial();
      if (tutorialCanBeShown) {
        startTutorial(TUTORIAL_ORDER_CLUSTER);
      }
    }
  }, [createdElement]);

  return (
    <>
      {tutorialState.activeTutorial === TUTORIAL_ORDER_CLUSTER && <OrderClusterTutorial />}
      <ClusterRenderer
        clusterData={clusters}
        supercluster={supercluster}
        onClusterClick={(cluster: any) => onMapClusterClickHandler(cluster)}
        onOrderMapMarkerClick={(order: any) => onOrderMapMarkerClick(order)}
        zoom={zoom}
      />

      {restaurant && (
        <RestaurantMapMarker
          onClick={() => setMapReFocusToCenter(true)}
          position={defaultMapMarkerPosition}
          restaurantInfo={restaurant}
        />
      )}

      {activeCouriers?.filter(courierListFilterLogic).map((c: ICourierSnap, index: number) => {
        const courierActiveStatus = courierStatus?.find(
          (s: IDeliveryStatus) => s.courierId === c.id
        )?.currentDeliveryStatus;
        const courierActiveOrder = getActiveOrderByCourierId(c.id);
        const courierActiveOrderETATime = courierActiveOrder
          ? getOrderEtaByUUID(courierActiveOrder?.uuid)
          : undefined;
        return (
          <CourierMapMarker
            courierName={c.firstName}
            courierId={c.id}
            courierReturnStatusType={courierActiveStatus as any}
            key={index}
            onClick={() => {
              onCourierMapMarkerClick(c);
            }}
            vehicleType={c.vehicleType}
            etaTime={courierActiveOrderETATime}
            compareTime={serverTime}
            dueTime={courierActiveOrder?.expectedDeliveryTime}
          />
        );
      })}
    </>
  );
};

export default DeliveryDashboardMapMarkers;
